import React, { useEffect, useState, lazy, Suspense } from 'react'
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom'
import { Grid, Container, CircularProgress, Box } from '@mui/material'
import './App.css'
import Header from './components/Header'
import Headline from './components/Headline'
import SidebarNav from './components/SidebarNav'
import Footer from './components/Footer'

// Lazy-loaded Seiten für bessere Performance
const Contracts = lazy(() => import('./pages/Contracts'))
const Dashboard = lazy(() => import('./pages/Dashboard'))
const Invoices = lazy(() => import('./pages/Invoices'))
const Payments = lazy(() => import('./pages/Payments'))
const Profile = lazy(() => import('./pages/Profile'))
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'))
const HelpAndContact = lazy(() => import('./pages/HelpAndContact'))
const Imprint = lazy(() => import('./pages/Imprint'))
const Login = lazy(() => import('./pages/Login'))
const MeterReadings = lazy(() => import('./pages/MeterReadings'))

import Cookies from 'js-cookie'
import axios from 'axios'
import { authUrl } from './utils/apiUrls'
import useAppStore from './hooks/useAppStore'
import usePageTitle from './hooks/usePageTitle'
import { fetchContractData } from './utils/fetchContractData'

interface AppProps {
  toggleTheme: () => void
  isDarkMode: boolean
}

/** Loading-Fallback für Suspense */
const PageLoader = () => (
  <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
    <CircularProgress />
  </Box>
) 


const App: React.FC<AppProps> = ({ toggleTheme, isDarkMode }) => {
  const navigate = useNavigate()
  const location = useLocation()

  const {
    contracts,
    contractData,
    contractDataItem,
    contractIsSelected,
    customerId,
    expirationDate,
    logoUrl,
    userName,
    brandName,
  } = useAppStore() 

  const pageTitle = usePageTitle(location)

  /* ------------------------------------------------------ */
  /* Set page title and favicon --------------------------- */
  /* ------------------------------------------------------ */

  useEffect(() => {
    document.title = 'Mein ' + brandName + ' Kundenportal'
  }, [])

  useEffect(() => {
    let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement
    if (!link) {
      link = document.createElement('link')
      link.rel = 'icon'
      document.getElementsByTagName('head')[0].appendChild(link)
    }
    link.href = '/favicon.ico'
  }, [])

  /* ------------------------------------------------------ */
  /* Login ------------------------------------------------ */
  /* ------------------------------------------------------ */

  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')

   const [fehlerText, setFehlerText] = useState<string>("");

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      const response = await axios.post(
        authUrl,
        {
          Username: username,
          Password: password
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'accept': '*/*'
          }
        }
      )
      setFehlerText("");
      Cookies.set('token', response.data, { sameSite: 'strict' })
      navigate('/')
    } catch (error) {
        if (axios.isAxiosError(error)) {
        if (error.response?.status === 401) {
          setFehlerText("Benutzername oder Passwort ist falsch.");
        } else {
          setFehlerText(`Login Fehler: ${error.message || ""}`);
        }
      } else {
        setFehlerText("Unerwarteter Fehler");
      }
      console.error(error)
    }
  }

  /* ------------------------------------------------------ */
  /* Logout ----------------------------------------------- */
  /* ------------------------------------------------------ */

  const clearSession = useAppStore(
    (state: unknown) => (state as { clearSessionStorage: () => void }).clearSessionStorage
  )

  const handleLogout = () => {
    Cookies.remove('token')
    clearSession()
    navigate('/')
  }

  /* ------------------------------------------------------ */
  /* Decode token, set contracts and times ---------------- */
  /* ------------------------------------------------------ */

  const currentDate = new Date().getTime()
  const token = Cookies.get('token')

  useEffect(() => {
    if (token) {
      /**
       * Parst ein JWT-Token und gibt den Payload zurück.
       * @throws Error wenn das Token ungültig ist
       */
      function parseJwt(token: string): Record<string, unknown> | null {
        try {
          // Validiere Token-Format (muss 3 Teile haben)
          const parts = token.split('.')
          if (parts.length !== 3) {
            console.error('Ungültiges Token-Format')
            return null
          }

          const base64Url = parts[1]
          const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
          const jsonPayload = decodeURIComponent(
            window
              .atob(base64)
              .split('')
              .map(function (c) {
                return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
              })
              .join('')
          )
          return JSON.parse(jsonPayload)
        } catch (error) {
          console.error('Fehler beim Parsen des Tokens:', error)
          return null
        }
      }

      const decodedToken = parseJwt(token)
      
      if (decodedToken) {
        useAppStore.setState({
          contracts: (decodedToken.Contracts as unknown[]) || [],
          expirationDate: ((decodedToken.exp as number) || 0) * 1000,
          logoUrl: (decodedToken.LogoRelativeUrl as string) || '',
          brandName: (decodedToken.BrandName as string) || '',
          userName: (decodedToken.UserName as string) || '',
          brandPrimaryColor: (decodedToken.BrandPrimaryColor as string) || '',
        })
      } else {
        // Bei ungültigem Token: Cookie entfernen und zur Login-Seite
        Cookies.remove('token')
      }
    }
  }, [token])

  /* ------------------------------------------------------ */
  /* Fetch all contract contents -------------------------- */
  /* ------------------------------------------------------ */


useEffect(() => {
  const fetchData = async () => {
    const data = await fetchContractData(contracts)
    useAppStore.setState({ contractData: data })
  }

  if (token) fetchData()
}, [token, contracts])

  /* ------------------------------------------------------ */
  /* Select single contract from dialog box --------------- */
  /* ------------------------------------------------------ */

const selectedContractId = useAppStore(state => state.selectedContractId)
const setSelectedContractId = useAppStore(state => state.setSelectedContractId)

const handleContractSelect = () => {
  if (!selectedContractId) {

    console.warn('Keine Vertragsnummer!')
    return
  }

  useAppStore.setState({
    contractIsSelected: true,
    selectedContractId, // ← kommt aus useState
    contractDataItem: contractData.find(
      (obj: { CustomerNumber: string }) => obj.CustomerNumber === selectedContractId
    )
  })
  toggleContractDialog()
}


const handleContractSelectOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
  setSelectedContractId(event.target.value)
  
}
  /* ------------------------------------------------------ */
  /* Select contract from dashboard card --------------- */
  /* ------------------------------------------------------ */

  const selectContractFromDashboard = (contractId: string) => {
    useAppStore.setState({
      contractIsSelected: true,
      selectedContractId: contractId,
      contractDataItem: contractData.find(
        (obj: { CustomerNumber: string }) => obj.CustomerNumber === contractId
      )
    })
  }

  /* ------------------------------------------------------ */
  /* Handle dialog modals --------------------------------- */
  /* ------------------------------------------------------ */

  const [isContractDialogOpen, setContractDialogOpen] = useState(false)
  const [isPaymentDialogOpen, setPaymentDialogOpen] = useState(false)
  const [isBillingDialogOpen, setBillingDialogOpen] = useState(false)
  const [isMeterReadingDialogOpen, setMeterReadingDialogOpen] = useState(false)
  const [isMeterNumberDialogOpen, _setMeterNumberDialogOpen] = useState(false)
  const [isPaymentRateDialogOpen, setPaymentRateDialogOpen] = useState(false)
  const [isContractAddDialogOpen, setIsContractAddDialogOpen] = useState(false)

  const toggleContractDialog = () => {
    setContractDialogOpen(!isContractDialogOpen)
  }

  const togglePaymentDialog = () => {
    setPaymentDialogOpen(!isPaymentDialogOpen)
  }

  const toggleBillingDialog = () => {
    setBillingDialogOpen(!isBillingDialogOpen)
  }

  const toggleMeterReadingDialog = () => {
    setMeterReadingDialogOpen(!isMeterReadingDialogOpen)
  }

  const togglePaymentRateDialog = () => {
    setPaymentRateDialogOpen(!isPaymentRateDialogOpen)
  }

  const toggleContractAddDialog = () => {
    setIsContractAddDialogOpen((prev) => !prev)
  }

  /* ------------------------------------------------------ */
  /* Component props -------------------------------------- */
  /* ------------------------------------------------------ */

  const commonProps = {
    contractData,
    contractDataItem,
    contractIsSelected,
    customerId,
    brandName,
    userName,
    logoUrl
  }

  const handlerProps = {
    isContractDialogOpen,
    toggleContractDialog,
    handleContractSelect,
    handleContractSelectOnChange,
    isBillingDialogOpen,
    toggleBillingDialog,
    isPaymentDialogOpen,
    togglePaymentDialog,
    handleLogin,
    handleLogout,
    isMeterReadingDialogOpen,
    isMeterNumberDialogOpen,
    isContractAddDialogOpen,
    toggleContractAddDialog,
    toggleMeterReadingDialog,
    // toggleMeterNumberDialog,
    isPaymentRateDialogOpen,
    togglePaymentRateDialog,
    selectContractFromDashboard,
    selectedContractId,
    setSelectedContractId
  }

  /* ------------------------------------------------------ */
  /* Render ------------------------------------------------ */
  /* ------------------------------------------------------ */

  if (!token || expirationDate < currentDate) {
    return (
     <Suspense fallback={<PageLoader />}>
       <Routes>
        <Route
          path="/"
          element={
            <Login
              logoUrl={logoUrl}
              username={username}
              password={password}
              setUsername={setUsername}
              setPassword={setPassword}
              handleLogin={handleLogin}
              fehlerText={fehlerText}
              authUrl={authUrl}
            />
          }
        />
        <Route path="/forgotPassword" element={<ForgotPassword />} />
      </Routes>
     </Suspense>
    )
  } else
    return (
      <div className="App">
        <Header handleLogout={handleLogout} userName={userName} toggleTheme={toggleTheme} isDarkMode={isDarkMode} />
        <Headline title={pageTitle} />
        <Container maxWidth={'xl'}>
          <Grid container columns={12} spacing={{ xs: 2, md: 4 }}>
            <Grid item xs={12} md={3}>
              <SidebarNav handleLogout={handleLogout} />
            </Grid>
            <Grid item xs={12} md={9}>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route
                    path="/"
                    element={<Dashboard {...commonProps} {...handlerProps} />}
                  />
                  <Route
                    path="/vertraege"
                    element={<Contracts {...commonProps} {...handlerProps} />}
                  />
                  <Route
                    path="/zaehlerstaende"
                    element={<MeterReadings {...commonProps} {...handlerProps} />}
                  />
                  <Route
                    path="/rechnungen"
                    element={<Invoices {...commonProps} {...handlerProps} />}
                  />
                  <Route
                    path="/abschlaege"
                    element={<Payments {...commonProps} {...handlerProps} />}
                  />
                  <Route
                    path="/profile"
                    element={<Profile />}
                  />

                  <Route path="/helpandcontact" element={<HelpAndContact />} />
                  <Route path="/imprint" element={<Imprint />} />
                </Routes>
              </Suspense>
            </Grid>
          </Grid>
        </Container>
        <Footer {...commonProps} />
      </div>
    )
}

export default App
